<script lang="ts" setup>
    import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
    import { useMounted, useResizeObserver } from "@vueuse/core";
    import VueHorizontal from "vue-horizontal";
    import CarretsWhite from "@icons/carrets-white.svg";

    const props = defineProps({
        rowClass: {
            type: Object,
            default() {
                return {};
            }
        },
        isMobile: {
            type: Boolean,
            default: false
        },
        itemCount: {
            type: Number,
            default: 0
        }
    });

    const isMounted = useMounted();
    const shellRef = ref<HTMLElement | null>(null);
    const carouselRef = ref<{
        scrollToIndex: (index: number) => void;
        $el: HTMLElement;
    } | null>(null);
    const activeIndex = ref(0);
    const hasOverflow = ref(false);
    const isPanning = ref(false);
    const indexFromClick = ref(false);
    let panTimer: ReturnType<typeof setTimeout> | undefined;

    const shellClass = computed(() => {
        const rowClass = (props.rowClass ?? {}) as Record<string, boolean>;
        return {
            "palette-row-shell": true,
            "palette-row-shell--carousel": true,
            "palette-row-shell--three": !!rowClass["palette-three-items"],
            "palette-row-shell--four": !!rowClass["palette-four-items"],
            "palette-row-shell--five": !!rowClass["palette-five-items"]
        };
    });

    const useCarousel = computed(() => {
        return isMounted.value && props.isMobile && props.itemCount > 1;
    });

    function carouselContainer(): HTMLElement | null {
        return (
            carouselRef.value?.$el?.querySelector(".v-hl-container") ?? null
        );
    }

    function updateOverflow() {
        const container = carouselContainer();
        if (!container) {
            hasOverflow.value = false;
            return;
        }
        hasOverflow.value = container.scrollWidth > container.clientWidth + 1;
    }

    function nearestIndex() {
        const container = carouselContainer();
        if (!container) {
            return activeIndex.value;
        }
        const edge = container.getBoundingClientRect().left;
        const items = Array.from(container.children) as HTMLElement[];
        let nearest = 0;
        let best = Number.POSITIVE_INFINITY;
        items.forEach((item, index) => {
            const distance = Math.abs(item.getBoundingClientRect().left - edge);
            if (distance < best) {
                best = distance;
                nearest = index;
            }
        });
        return nearest;
    }

    function onCarouselScroll() {
        isPanning.value = true;
        clearTimeout(panTimer);
        panTimer = setTimeout(() => {
            isPanning.value = false;
            if (!indexFromClick.value) {
                activeIndex.value = nearestIndex();
            }
            indexFromClick.value = false;
            updateOverflow();
        }, 200);
    }

    function pan(direction: 1 | -1, event: Event) {
        event.preventDefault();
        const nextIndex = Math.min(
            props.itemCount - 1,
            Math.max(0, activeIndex.value + direction)
        );
        if (nextIndex === activeIndex.value) {
            return;
        }
        carouselRef.value?.scrollToIndex(nextIndex);
        activeIndex.value = nextIndex;
        indexFromClick.value = true;
        onCarouselScroll();
    }

    useResizeObserver(shellRef, () => {
        nextTick(updateOverflow);
    });

    onMounted(() => {
        nextTick(updateOverflow);
    });

    onBeforeUnmount(() => {
        clearTimeout(panTimer);
    });

    watch(
        () => props.itemCount,
        () => {
            activeIndex.value = 0;
            nextTick(updateOverflow);
        }
    );

    const canShowControls = computed(() => {
        return useCarousel.value && hasOverflow.value && !isPanning.value;
    });

    const showPrevious = computed(() => {
        return canShowControls.value && activeIndex.value > 0;
    });

    const showNext = computed(() => {
        return canShowControls.value && activeIndex.value < props.itemCount - 1;
    });
</script>

<template>
    <div ref="shellRef" :class="shellClass">
        <vue-horizontal
            v-if="useCarousel"
            ref="carouselRef"
            :class="rowClass"
            :button="false"
            scroll
            snap="start"
            @scroll="onCarouselScroll"
        >
            <slot />
        </vue-horizontal>
        <div v-else :class="rowClass">
            <slot />
        </div>
        <template v-if="isMounted">
            <button
                v-if="showPrevious"
                type="button"
                class="scroll-more-indicator scroll-more-indicator--left"
                aria-label="Show previous category"
                @click="pan(-1, $event)"
            >
                <CarretsWhite alt="" />
            </button>
            <button
                v-if="showNext"
                type="button"
                class="scroll-more-indicator scroll-more-indicator--right"
                aria-label="Show next category"
                @click="pan(1, $event)"
            >
                <CarretsWhite alt="" />
            </button>
        </template>
    </div>
</template>
